import { SearchForm } from '@/features/SearchForm'
import { BasePage } from '@/pages/BasePage'
import { DestinationTable } from '@/features/DestinationTable'
import { css, Theme } from '@emotion/react'
import { useFetch } from '@/hooks/useFetch'
import { ChangeEventHandler, useEffect, useRef, useState } from 'react'
import type { PaginationData } from '@/types/pagination'
import { UseFetchResult } from '@/types/useFetchResult'
import { MainTitle } from '@/components/title/MainTitle'
import { showError, showSuccess } from '@/shared/toast'
import { DELETE } from '@/api/api'
import { DestinationsList } from '@/types/destinationsList'
import { BaseButton } from '@/components/button/BaseButton'
import { useNavigate } from 'react-router-dom'
import { ListPagination } from '@/components/pagination/ListPagination'
import { CsvMenu } from '@/features/CsvMenu'
import { SortConfig } from '@/components/table/BaseTable'
import { tableName } from '@/types/tableName'

const pageStyles = css`
  display: flex;
  flex-direction: column;
`

const titleRowStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const titleWithLegendStyles = css`
  display: flex;
  align-items: center;
  gap: 24px;
`

const legendStyles = (t: Theme) => css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  font-size: 1.4rem;
  color: ${t.c.bc.text};
`

const legendItemStyles = css`
  display: flex;
  align-items: center;
`

const legendIconStyles = css`
  width: 12px;
  height: 12px;
  display: inline-block;
  margin-right: 4px;
`

const newButtonStyle = css`
  width: 121px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

const iconStyle = css`
  width: 16px;
  height: 16px;
  fill: currentColor;
  flex-shrink: 0;
`

const searchRowStyles = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  gap: 16px;
`

const paginationGroupStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  margin-left: auto;
`

const tableContainerStyles = css`
  margin-top: 16px;
`

const bottomPaginationStyles = css`
  margin-top: 16px;
  display: flex;
  justify-content: right;
`

const DEFAULT_PER_PAGE = 30

export const DestinationsListPage = () => {
  const navigate = useNavigate()
  const [pagination, setPagination] = useState<
    PaginationData<DestinationsList>
  >({
    data: [],
    currentPage: 1,
    lastPage: 1,
    total: 0,
    from: 0,
    to: 0,
    perPage: DEFAULT_PER_PAGE,
  })
  const [searchType, setSearchType] = useState('1')
  const [keyword, setKeyword] = useState('')
  const [isNeedObsoleteChecked, setIsNeedObsoleteChecked] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'start_date',
    order: 'desc',
  })

  const paginationKeywordRef = useRef<string>('')
  const isNeedObsoleteRef = useRef<boolean>(false)
  const isPaginationRef = useRef<boolean>(false)

  const handleChange: ChangeEventHandler<
    HTMLInputElement | HTMLSelectElement
  > = (event) => {
    const { name, value } = event.target

    if (name === 'searchType') {
      setSearchType(value)
      setKeyword('')
    } else if (name === 'keyword') {
      setKeyword(value)
    } else if (event.target.type === 'checkbox') {
      if (name === 'isNeedObsolete') {
        setIsNeedObsoleteChecked(event.target.checked)
      }
    }
  }

  const handleClick = () => {
    paginationKeywordRef.current = keyword
    isNeedObsoleteRef.current = isNeedObsoleteChecked
    isPaginationRef.current = false
    setErrorMessage('')

    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }))

    refetch()
  }

  const handlePageChange = (page: number) => {
    isPaginationRef.current = true
    setErrorMessage('')
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }))

    refetch()
  }

  const handlePerPageChange = (perPage: number) => {
    isPaginationRef.current = true
    setErrorMessage('')
    setPagination((prev) => ({
      ...prev,
      perPage,
      currentPage: 1,
    }))

    refetch()
  }

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      order: prev.key === key && prev.order === 'desc' ? 'asc' : 'desc',
    }))
    refetch()
  }

  const { data, error, refetch }: UseFetchResult = useFetch(
    `/api/destinations?page=${pagination.currentPage}&per_page=${
      pagination.perPage
    }&keyword=${
      isPaginationRef.current ? paginationKeywordRef.current : keyword
    }&is_need_obsolete=${
      isPaginationRef.current
        ? isNeedObsoleteRef.current
        : isNeedObsoleteChecked
    }&sort_key=${sortConfig.key}&sort_order=${sortConfig.order}`,
  )

  // TODO:  APIのレスポンス形式を統一する
  // 現在は、Destination、HealthInsurerとuserで異なるため、条件分岐している
  useEffect(() => {
    if (data && 'items' in data) {
      setPagination((prev) => ({
        ...prev,
        data: data.items,
        currentPage: data.pagination.currentPage,
        lastPage: data.pagination.lastPage,
        total: data.pagination.total,
        from: data.pagination.from,
        to: data.pagination.to,
        perPage: data.pagination.perPage,
      }))
    }
  }, [data])

  useEffect(() => {
    if (error) {
      showError('送達先一覧の取得に失敗しました。')
    }
  }, [error])

  return (
    <BasePage title="送達先一覧">
      <div css={pageStyles}>
        <div css={titleRowStyles}>
          <div css={titleWithLegendStyles}>
            <MainTitle>送達先一覧</MainTitle>
            <div css={legendStyles}>
              <div css={legendItemStyles}>
                <img
                  src="/images/window-restore-icon.svg"
                  css={legendIconStyles}
                  alt="適用待ち情報あり"
                />
                ：適用待ち情報あり
              </div>
              <div css={legendItemStyles}>
                <img
                  src="/images/attention-icon.svg"
                  css={legendIconStyles}
                  alt="廃止予定"
                />
                ：廃止予定
              </div>
            </div>
          </div>
          <BaseButton
            color="outline"
            size="fixed38"
            addStyle={newButtonStyle}
            onClick={() => navigate('new')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              css={iconStyle}
            >
              <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM232 344l0-64-64 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l64 0 0-64c0-13.3 10.7-24 24-24s24 10.7 24 24l0 64 64 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-64 0 0 64c0 13.3-10.7 24-24 24s-24-10.7-24-24z" />
            </svg>
            新規登録
          </BaseButton>
        </div>
        <div css={searchRowStyles}>
          <SearchForm
            helpText={
              '下記の項目を対象に部分一致検索を行います' +
              '\n' +
              '「送達先番号」「送達先名」のいずれか'
            }
            keyword={keyword}
            onChange={handleChange}
            onClick={handleClick}
            errorMessage={errorMessage}
            isNeedObsoleteVisible={true}
            isNeedObsoleteChecked={isNeedObsoleteChecked}
          />
          <div css={paginationGroupStyles}>
            <ListPagination
              currentPage={pagination.currentPage}
              lastPage={pagination.lastPage}
              from={pagination.from}
              to={pagination.to}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
            <CsvMenu tableName={tableName.DESTINATIONS} />
          </div>
        </div>
        <div css={tableContainerStyles}>
          <DestinationTable
            data={pagination.data}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
          <div css={bottomPaginationStyles}>
            <ListPagination
              currentPage={pagination.currentPage}
              lastPage={pagination.lastPage}
              from={pagination.from}
              to={pagination.to}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </div>
        </div>
      </div>
    </BasePage>
  )
}
